import { createClient } from 'contentful';

const client = createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE_ID || '',
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN || '',
  environment: process.env.REACT_APP_CONTENTFUL_ENVIRONMENT || 'master',
});

export interface ContentfulEntry {
  sys: {
    id: string;
    createdAt: string;
    updatedAt: string;
  };
  fields: {
    title: string;
    shortDescription: string;
    categoryCard: string;
    coverImage?: {
      sys: {
        id: string;
      };
    };
  };
}

export interface TrendingItem {
  id: string;
  category: string;
  timeAgo: string;
  title: string;
  description: string;
  source: string;
  imageUrl: string;
  link: string;
  authorName: string;
  contentType: 'article' | 'book' | 'film';
}

const formatCreatedAtDate = (date: string): string => {
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const parseFilmReelFeed = (xmlText: string): TrendingItem[] => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, 'application/xml');
  const items = Array.from(xmlDoc.querySelectorAll('channel > item')).slice(0, 2);

  return items.map((item) => {
    const title = item.querySelector('title')?.textContent?.trim() || 'Untitled Film';
    const link = item.querySelector('link')?.textContent?.trim() || '';
    const description = item.querySelector('description')?.textContent?.trim() || '';
    const authorName =
      item.querySelector('creator')?.textContent?.trim() ||
      item.querySelector('author')?.textContent?.trim() ||
      'FilmReel';
    const pubDate = item.querySelector('pubDate')?.textContent?.trim() || '';
    const enclosure = item.querySelector('enclosure');
    const imageUrl = enclosure?.getAttribute('url') || '';

    return {
      id: link || title,
      category: 'FilmReel',
      timeAgo: pubDate ? formatCreatedAtDate(pubDate) : '',
      title,
      description,
      source: 'FilmReel',
      imageUrl,
      link,
      authorName,
      contentType: 'film',
    };
  });
};


    const extractPlainTextFromRichText = (richTextDoc: any): string => {
      try {
        if (!richTextDoc?.content) return '';
        return richTextDoc.content
          .map((block: any) => {
            if (block.content) {
              return block.content.map((inline: any) => inline.value || '').join('');
            }
            return '';
          })
          .join(' ')
          .trim();
      } catch {
        return '';
      }
    };


    const createSlug = (text: string): string => {
      return text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
    };

export const fetchLatestTrending = async (): Promise<TrendingItem[]> => {
  try {
    const response = await client.getEntries({
      content_type: 'landingPage',
      order: ['-sys.updatedAt'],
      limit: 5,
      include: 2, 
    });

    const trendingItems: TrendingItem[] = await Promise.all(
      response.items.map(async (item: any) => {
        const fields = item.fields;
        let imageUrl = '';
        if (fields.coverImage?.sys?.id) {
          try {
            const asset = await client.getAsset(fields.coverImage.sys.id);
            imageUrl = `https:${(asset.fields as any).file.url}`;
          } catch (error) {
            console.error('Error fetching asset:', error);
          }
        }

      
        const templates: any[] = Array.isArray(fields.templates) ? fields.templates : [];
        const newspaperTemplate = templates.find(
          (t: any) => t.sys?.contentType?.sys?.id === 'newspaperTemplate'
        );
        const bookTemplate = templates.find(
          (t: any) => t.sys?.contentType?.sys?.id === 'bookTemplate'
        );
        let title = 'Untitled';
        let authorNameRaw = '';
        let contentType: 'article' | 'book' = 'article';

        if (newspaperTemplate) {
          title = extractPlainTextFromRichText(newspaperTemplate.fields.newspaperTitleRich) || 'Untitled';
          authorNameRaw = newspaperTemplate.fields?.authorName || '';
          contentType = 'article';
        } else if (bookTemplate) {
          title = bookTemplate.fields?.booktitle || 'Untitled';
          authorNameRaw = bookTemplate.fields?.authorName || '';
          contentType = 'book';
        }

        const authorName = authorNameRaw.startsWith('By ') ? authorNameRaw.slice(3) : authorNameRaw;
        const slug = createSlug(title);
        const category = fields.categoryCard?.split(',')[0]?.trim() || 'General';
        const link = `https://www.independentpress.com/${contentType}/${slug}`;

        return {
          id: item.sys.id,
          category,
          timeAgo: formatCreatedAtDate(item.sys.createdAt),
          title,
          description: fields.shortDescription || '',
          source: 'Independent Press',
          imageUrl,
          link,
          authorName,
          contentType,
        };
      })
    );

    return trendingItems;
  } catch (error) {
    console.error('Error fetching trending data:', error);
    return [];
  }
};

export const fetchLatestFilmReel = async (): Promise<TrendingItem[]> => {
  try {
    const response = await fetch(
      'https://www.filmreel.com/feed.xml'
    );
    const xmlText = await response.text();
    return parseFilmReelFeed(xmlText);
  } catch (error) {
    console.error('Error fetching FilmReel feed:', error);
    return [];
  }
};